<template>
  <div
    :class="[
      'w-full flex flex-col max-h-[85vh]',
      createSchedule ? 'gap-6' : 'gap-7',
      fullWidthModal ? 'p-2' : 'p-6 sm:p-8'
    ]"
  >
    <!-- No wizard o espaço título → conteúdo segue os 24px das demais etapas (gap-6). -->
    <ScheduleStepHeading v-if="createSchedule" :title="SCHEDULE_SECTION_LABELS.attendancePolicy" />
    <p v-else class="text-[24px] font-bold text-[#16161d] text-left m-0">
      {{
        isEditing
          ? 'Editar política de registro de plantão'
          : 'Nova política de registro de plantão'
      }}
    </p>

    <!--
      Cadeado da Q31 (M4, Tela 2). Ergonomia, não segurança: o backend rejeita o payload
      das seções travadas de qualquer jeito (§1.6). Vigência, modalidade, tolerâncias,
      folhas e modelos seguem a permissão antiga — quem já editava política continua
      editando o resto.
    -->
    <div
      v-if="!canEditRegistration || !canEditAudit"
      class="flex items-start gap-2 rounded-lg border border-negative/40 bg-negative/5 px-3 py-2.5 text-left text-sm text-negative"
      data-testid="policy-registration-lock-banner"
      role="alert"
    >
      <span aria-hidden="true">🔒</span>
      <span>
        <b>Você não tem permissão para editar regras de registro e auditoria.</b>
        As seções abaixo estão em modo leitura. Peça a um administrador da empresa a permissão
        <i>“Editar registro e auditoria da política”</i>.
      </span>
    </div>

    <div class="flex-1 overflow-y-auto pr-8 pb-3">
      <div class="mb-3" data-testid="create-schedule-policy-nome-input">
        <EditableHeader
          v-model="policyName"
          :subtitle="policySubtitle"
          placeholder="Nome da Política de Registro de Plantão"
          :is-initial-editing="!isEditing"
          :error-messages="errors.name"
          @save="handleHeaderSave"
          @clear-error="delete errors.name"
        />
      </div>

      <!-- Fora do cadeado da Q31 (M4, balão ⑩): quem já editava política continua
             editando identificação e vigência. -->
      <div
        class="flex flex-wrap items-start gap-4 mb-2 mt-4"
        data-testid="policy-section-identification"
      >
        <div
          class="w-full sm:w-auto min-h-[56px]"
          data-testid="create-schedule-policy-vigencia-inicio-input"
        >
          <DatePicker
            v-model="startDate"
            locale="pt-BR"
            :masks="{ input: 'DD/MM/YYYY' }"
            :max-date="scheduleMaxDate"
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                placeholder="Início da vigência"
                :append-inner-icon="mdiCalendar"
                :value="inputValue"
                v-on="inputEvents"
                density="comfortable"
                variant="outlined"
                color="primary20"
                class="w-full sm:w-[192px]"
                :error-messages="errors.startDate"
              />
            </template>
          </DatePicker>
        </div>

        <div class="w-full sm:w-auto min-h-[56px]">
          <DatePicker
            v-model="endDate"
            locale="pt-BR"
            :masks="{ input: 'DD/MM/YYYY' }"
            :disabled="isIndeterminate"
            :max-date="scheduleMaxDate"
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                placeholder="Fim da vigência"
                :append-inner-icon="mdiCalendar"
                :value="inputValue"
                v-on="inputEvents"
                density="comfortable"
                variant="outlined"
                color="primary20"
                class="w-full sm:w-[192px]"
                :disabled="isIndeterminate"
                :error-messages="errors.endDate"
              />
            </template>
          </DatePicker>
        </div>

        <div
          class="flex items-center gap-2 h-[48px] w-full sm:w-auto"
          data-testid="create-schedule-policy-vigencia-indeterminado-checkbox"
        >
          <DsSwitch v-model="isIndeterminate" />
          <label class="text-sm font-medium text-gray-700 whitespace-nowrap">
            Fim de vigência indeterminado
          </label>
        </div>
      </div>

      <div class="flex flex-wrap gap-4" data-testid="create-schedule-policy-modalidade-select">
        <v-select
          v-model="monitoringType"
          label="Modalidade de registro"
          :items="DSATTENDANCE_MONITORING_TYPE_OPTIONS"
          item-title="title"
          item-value="value"
          variant="outlined"
          density="comfortable"
          color="primary20"
          class="flex-1 min-w-[280px]"
          :error-messages="errors.monitoringType"
        />

        <v-text-field
          v-if="monitoringType === DSAttendanceMonitoringType.EXTERNAL_APP"
          v-model="externalApp"
          label="Nome do aplicativo"
          variant="outlined"
          density="comfortable"
          color="primary20"
          class="flex-1 min-w-[200px]"
          :error-messages="errors.externalApp"
          @update:model-value="delete errors.externalApp"
        />
      </div>

      <v-expand-transition>
        <div v-if="monitoringType === DSAttendanceMonitoringType.MINHA_ESCALA_APP">
          <p class="text-neutral50 font-bold text-left text-md mb-1 mt-4 flex items-center gap-2">
            <span>Verificações obrigatórias</span>
            <span
              v-if="showInheritanceHint"
              class="rounded-full bg-primary5 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-primary40"
              data-testid="policy-check-inherited-badge"
            >
              herdado da escala
            </span>
          </p>
          <!--
            M7, balão ⑤ — o estado inicial NÃO é "desligado": ele é o que a escala exige
            hoje. Foi o "desligado por omissão" que derrubou o check-in em 229 escalas.
          -->
          <p
            v-if="showInheritanceHint"
            class="text-xs text-neutral50 text-left m-0 mb-3"
            data-testid="policy-check-inherited-help"
          >
            Começamos com o que esta escala exige hoje. Altere aqui — a partir da vigência, vale o
            que estiver nesta política.
          </p>
          <div v-else class="mb-3"></div>
          <div class="flex flex-wrap gap-4 items-stretch">
            <!--
              Q3 (spec 2026-09-25 §6.6) — fora do time nunca exige check. Desabilitado e
              sempre desligado: o backend grava `false` de qualquer jeito, e um controle
              clicável prometeria um estado que o servidor descarta.
            -->
            <div
              class="flex flex-col min-w-[240px] flex-1 border border-gray-200 rounded-lg px-4 py-3 opacity-70"
              :title="NOT_OUR_TEAM_CHECK_DISABLED_REASON"
              data-testid="policy-check-externos-card"
            >
              <div class="flex-1 flex items-center">
                <v-checkbox
                  :model-value="false"
                  data-testid="policy-check-externos-checkbox"
                  label="Habilitar check para externos"
                  density="compact"
                  color="primary"
                  hide-details="auto"
                  disabled
                />
              </div>
              <p
                class="m-0 mt-1 text-[11px] font-bold text-neutral50 text-left"
                data-testid="policy-check-externos-disabled-hint"
              >
                {{ NOT_OUR_TEAM_CHECK_DISABLED_REASON }}
              </p>
            </div>
            <div
              class="flex flex-col min-w-[240px] flex-1 border border-gray-200 rounded-lg px-4 py-3"
            >
              <div class="flex-1 flex items-center">
                <v-checkbox
                  v-model="requiredCheckOurTeam"
                  data-testid="policy-check-nosso-time-checkbox"
                  label="Habilitar check para nosso time"
                  density="compact"
                  color="primary"
                  hide-details="auto"
                  :error-messages="errors.requiredCheckOurTeam"
                  @change="onCheckToggled('ourTeam')"
                />
              </div>
              <p
                v-if="isInheritedOurTeam"
                class="m-0 mt-1 text-[11px] font-bold text-primary40 text-left"
                data-testid="policy-check-nosso-time-inherited-tag"
              >
                ↧ herdado da configuração atual da escala
              </p>
              <p
                v-else-if="ourTeamTouched"
                class="m-0 mt-1 text-[11px] font-bold text-alert text-left"
                data-testid="policy-check-nosso-time-changed-tag"
              >
                alterado nesta política
              </p>
              <v-expand-transition>
                <div v-if="requiredCheckOurTeam" class="mt-3">
                  <v-select
                    v-model="checkMethodOurTeam"
                    data-testid="policy-check-nosso-time-method-select"
                    label="Método de check"
                    :items="CHECK_METHOD_OPTIONS"
                    item-title="title"
                    item-value="value"
                    variant="outlined"
                    density="comfortable"
                    color="primary20"
                    :hint="!checkMethodOurTeam ? 'Selecione o método de check' : ''"
                    :persistent-hint="!checkMethodOurTeam"
                    :error-messages="errors.checkMethodOurTeam"
                    @update:model-value="delete errors.checkMethodOurTeam"
                  />
                  <PermanenceToggleField
                    v-if="flags.enabledPermanenceRecord"
                    :model-value="autoPermanenceOurTeam"
                    @update:model-value="autoPermanenceOurTeam = $event"
                    data-testid="policy-permanence-our-team-switch"
                  />
                </div>
              </v-expand-transition>
            </div>
          </div>
        </div>
      </v-expand-transition>

      <!--
        M7, balão ⑧ — Folha / App externo / Folha não diária escondem o bloco de check e
        dizem, com todas as letras, que o botão do app some. Hoje essa consequência não
        está escrita em lugar nenhum da tela.
      -->
      <div
        v-if="monitoringType !== DSAttendanceMonitoringType.MINHA_ESCALA_APP"
        class="mt-4 flex items-start gap-2 rounded-lg border border-alert/40 bg-alert/10 px-3 py-2.5 text-left text-sm text-alert"
        data-testid="policy-check-modality-warning"
        role="alert"
      >
        <span aria-hidden="true">⚠️</span>
        <span>
          <b class="block">O check no app deixa de ser exigido nesta escala.</b>
          Nesta modalidade o médico não registra pelo app: ao salvar, o botão de check-in some para
          todos os plantões a partir da vigência. O registro passa a chegar pela folha ou pelo
          sistema externo.
        </span>
      </div>

      <!--
        M7, Tela 2 (balões ⑩–⑬) — o aviso de quem DESLIGA um check que hoje é exigido, seja
        pela política vigente, seja pela flag legada da escala. O texto conta o EFEITO ("os
        médicos deixam de ver o botão"), nomeia a data de vigência e trava o salvar até a
        confirmação. Quando o check já estava desligado, nada aparece — o aviso precisa
        continuar significando alguma coisa.
      -->
      <div
        v-if="needsCheckDisableConfirmation"
        class="mt-4 flex items-start gap-2 rounded-lg border border-[#E3CE7C] bg-[#FBF3D3] px-3 py-2.5 text-left text-sm text-[#5C4700]"
        data-testid="policy-check-disable-warning"
        role="alert"
      >
        <span aria-hidden="true">⚠️</span>
        <div>
          <b class="block">
            Hoje esta escala exige check no app para {{ disabledAudiencesText }}.
          </b>
          <span>
            Ao salvar, os médicos deixam de ver o botão de check-in
            <template v-if="policyVigencyLabel">
              a partir de <strong>{{ policyVigencyLabel }}</strong> (início da vigência desta
              política).
            </template>
            <template v-else>a partir do início da vigência desta política.</template>
            Os plantões anteriores continuam como estão.
          </span>
          <label class="mt-2 flex items-start gap-2 text-xs font-bold">
            <input
              type="checkbox"
              class="mt-0.5 h-4 w-4 accent-primary30"
              data-testid="policy-check-disable-confirm"
              :checked="checkDisableConfirmed"
              @change="onDisableConfirmChange"
            />
            <span>Entendi que o check-in deixa de ser exigido</span>
          </label>
        </div>
      </div>

      <div
        v-if="
          monitoringType !== DSAttendanceMonitoringType.EXTERNAL_APP &&
          monitoringType !== DSAttendanceMonitoringType.NON_DAILY_SHEET
        "
        class="mt-6 mb-4"
      >
        <p class="text-neutral50 font-bold text-left text-md mb-3">Tolerâncias de registro</p>
        <div class="flex flex-wrap gap-4 w-full">
          <v-text-field
            v-model.number="checkinLateToleranceMinutes"
            label="Tolerância de atraso"
            type="number"
            variant="outlined"
            density="comfortable"
            color="primary20"
            class="w-full sm:w-[240px]"
            suffix="minutos"
            :error-messages="errors.checkinLateToleranceMinutes"
            @input="delete errors.checkinLateToleranceMinutes"
          />

          <v-text-field
            v-model.number="checkoutEarlyToleranceMinutes"
            label="Tolerância de saída antecipada"
            type="number"
            variant="outlined"
            density="comfortable"
            color="primary20"
            class="w-full sm:w-[280px]"
            suffix="minutos"
            :error-messages="errors.checkoutEarlyToleranceMinutes"
            @input="delete errors.checkoutEarlyToleranceMinutes"
          />
        </div>
      </div>
      <!--
        Fase 2 do fluxo de auditoria (spec 2026-09-11, §6.1) — seção "Auditoria". Visível em
        qualquer modalidade: a aprovação automática vale para todas (o rollout começa nas de
        app), e esconder o bloco por modalidade tornaria a configuração invisível justo onde
        ela é a mais barata de ligar.
      -->
      <div class="mt-6 mb-4" data-testid="policy-section-audit">
        <div data-testid="policy-audit-section">
          <p class="text-neutral50 font-bold text-left text-md mb-3 flex items-center gap-2">
            <span>Auditoria</span>
            <span
              v-if="!canEditAudit"
              class="rounded-full border border-negative/40 bg-negative/10 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-negative"
              data-testid="policy-section-audit-lock"
            >
              🔒 leitura
            </span>
          </p>
          <!--
          `inert` (e não `pointer-events-none`) porque o requisito é campo NÃO FOCÁVEL:
          o `SegmentedControl` é feito de `button`s, que continuariam alcançáveis por Tab.
          `undefined` remove o atributo — `inert="false"` ainda é inerte no navegador.
        -->
          <div class="flex flex-col gap-5" :inert="!canEditAudit || undefined">
            <SegmentedControl
              v-model="sheetConferenceBy"
              label="Quem confere a folha contra o espelho"
              :options="SHEET_CONFERENCE_BY_OPTIONS"
              :help="SHEET_CONFERENCE_BY_HELP"
              test-id-prefix="policy-sheet-conference-by"
            />

            <div class="flex flex-col gap-1 text-left">
              <SegmentedControl
                v-model="autoApproval"
                label="Aprovação automática"
                :options="AUTO_APPROVAL_OPTIONS"
                test-id-prefix="policy-auto-approval"
              />
              <p class="text-xs text-neutral50 m-0" data-testid="policy-auto-approval-criteria">
                {{ autoApprovalCriteriaText }}
              </p>
            </div>

            <!-- Sem aprovação automática não há o que sortear: o motor nem lê a taxa. -->
            <v-expand-transition>
              <div
                v-if="autoApproval === DSAttendanceAutoApproval.FULL_MATCH"
                class="w-full sm:w-[320px]"
              >
                <v-text-field
                  v-model.number="manualReviewSampleRate"
                  data-testid="policy-manual-review-sample-rate-input"
                  label="Revisão humana por amostragem"
                  type="number"
                  min="0"
                  max="100"
                  variant="outlined"
                  density="comfortable"
                  color="primary20"
                  suffix="%"
                  :disabled="!canEditAudit"
                  :error-messages="errors.manualReviewSampleRate"
                  @input="delete errors.manualReviewSampleRate"
                />
                <p class="text-xs text-neutral50 text-left m-0">
                  {{ MANUAL_REVIEW_SAMPLE_RATE_HELP }}
                </p>
              </div>
            </v-expand-transition>

            <!--
              Spec 2026-10-01 §7.4 (CA8) — "Auditoria do registro". Visível em TODAS as
              modalidades (a auditoria existe em todas) e sob o mesmo gate do bloco (`canEditAudit`).
            -->
            <div
              class="rounded-lg border border-primary20 bg-primary5/40 px-4 pb-1 pt-3 text-left"
              data-testid="policy-audit-deadline"
            >
              <p class="m-0 mb-1 text-sm font-bold text-textPrimary">Auditoria do registro</p>
              <p class="m-0 mb-3 text-xs text-neutral50">{{ AUDIT_DEADLINE_HELP }}</p>
              <div class="flex w-full flex-wrap gap-4">
                <v-text-field
                  v-model.number="auditDeadlineHours"
                  data-testid="policy-audit-deadline-hours-input"
                  label="Prazo de auditoria (horas)"
                  type="number"
                  min="0"
                  max="8760"
                  variant="outlined"
                  density="comfortable"
                  color="primary20"
                  suffix="horas"
                  placeholder="Sem prazo"
                  persistent-placeholder
                  class="min-w-[220px] flex-1"
                  :disabled="!canEditAudit"
                  :error-messages="errors.auditDeadlineHours"
                  @input="delete errors.auditDeadlineHours"
                />
                <v-select
                  v-model="auditDeadlineCalculationType"
                  data-testid="policy-audit-deadline-calculation-select"
                  label="Cálculo de horas"
                  :items="DSATTENDANCE_DEADLINE_CALCULATION_OPTIONS"
                  item-title="title"
                  item-value="value"
                  variant="outlined"
                  density="comfortable"
                  color="primary20"
                  class="min-w-[220px] flex-1"
                  clearable
                  :disabled="!canEditAudit"
                  :error-messages="errors.auditDeadlineCalculationType"
                  @update:model-value="delete errors.auditDeadlineCalculationType"
                />
              </div>
              <p
                v-if="auditDeadlinePreview"
                class="m-0 mb-3 text-xs text-primary30"
                data-testid="policy-audit-deadline-preview"
              >
                {{ auditDeadlinePreview }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <v-expand-transition>
        <div
          v-if="
            [
              DSAttendanceMonitoringType.FOLHA,
              DSAttendanceMonitoringType.EXTERNAL_APP,
              DSAttendanceMonitoringType.NON_DAILY_SHEET
            ].includes(monitoringType)
          "
        >
          <div class="flex flex-wrap gap-4 w-full mt-4">
            <v-text-field
              v-model.number="submissionDeadlineHours"
              label="Limite de envio"
              type="number"
              variant="outlined"
              density="comfortable"
              color="primary20"
              suffix="horas"
              :error-messages="errors.submissionDeadlineHours"
              @input="delete errors.submissionDeadlineHours"
            />
            <v-select
              v-model="deadlineCalculationType"
              label="Cálculo de horas"
              :items="DSATTENDANCE_DEADLINE_CALCULATION_OPTIONS"
              item-title="title"
              item-value="value"
              variant="outlined"
              density="comfortable"
              color="primary20"
              :error-messages="errors.deadlineCalculationType"
              @update:model-value="delete errors.deadlineCalculationType"
            />
          </div>

          <div v-if="showRulesAndTemplates" class="mt-4">
            <template
              v-if="
                monitoringType !== DSAttendanceMonitoringType.EXTERNAL_APP &&
                monitoringType !== DSAttendanceMonitoringType.NON_DAILY_SHEET
              "
            >
              <p class="text-primary50 font-bold text-left mb-4 text-md">Folhas da escala</p>
              <div
                v-for="(rule, index) in rules"
                :key="index"
                class="mb-8 border-b border-gray-100 pb-6 last:border-0"
              >
                <v-text-field
                  v-model="rule.name"
                  label="Nome da folha"
                  variant="outlined"
                  density="comfortable"
                  class="w-full"
                  :error-messages="errors[`rules[${index}].name`]"
                  @input="delete errors[`rules[${index}].name`]"
                />

                <div class="flex flex-wrap items-start gap-4 mb-2">
                  <v-select
                    v-model="rule.shiftIds"
                    label="Turnos"
                    :items="dynamicShiftOptions"
                    item-title="title"
                    item-value="value"
                    variant="outlined"
                    density="comfortable"
                    class="w-full sm:w-[250px]"
                    multiple
                    chips
                    closable-chips
                    :error-messages="errors[`rules[${index}].shiftIds`]"
                    @update:model-value="delete errors[`rules[${index}].shiftIds`]"
                  />
                  <v-select
                    v-model="rule.recurrence"
                    label="Recorrência"
                    :items="DSATTENDANCE_RECURRENCE_OPTIONS"
                    item-title="title"
                    item-value="value"
                    variant="outlined"
                    density="comfortable"
                    class="w-full sm:w-[150px]"
                    :error-messages="errors[`rules[${index}].recurrence`]"
                    @update:model-value="delete errors[`rules[${index}].recurrence`]"
                  />
                  <outlined-button
                    text="Excluir"
                    width="110px"
                    height="48px"
                    class="!border-negative !text-negative !bg-white"
                    :prepend-icon="mdiTrashCanOutline"
                    @click="removeRule(index)"
                  />
                </div>

                <v-text-field
                  v-model.number="rule.dayOfMonth"
                  label="Dia do mês"
                  type="number"
                  variant="outlined"
                  density="comfortable"
                  class="w-full sm:w-[192px]"
                  :disabled="rule.recurrence !== DSAttendanceRuleRecurrence.MONTHLY"
                  :error-messages="errors[`rules[${index}].dayOfMonth`]"
                  @input="delete errors[`rules[${index}].dayOfMonth`]"
                />

                <OptionSelect
                  label="Selecione os dias da semana"
                  :options="dayOptions"
                  v-model="rule.daysOfWeek"
                  multiple
                  :error-messages="errors[`rules[${index}].daysOfWeek`]"
                  @update:model-value="delete errors[`rules[${index}].daysOfWeek`]"
                />
              </div>
              <outlined-button
                text="Nova folha"
                width="140px"
                :prepend-icon="mdiPlus"
                @click="addRule"
              />
              <p v-if="errors.rules" class="text-red-600 text-xs mt-1">{{ errors.rules }}</p>
            </template>

            <div class="mt-4">
              <p v-if="templates.length" class="text-primary50 font-bold text-left mb-4 text-md">
                Modelos da folha
              </p>
              <div
                v-for="(template, idx) in templates"
                :key="idx"
                class="flex items-center gap-4 mb-4"
              >
                <div class="flex-1 text-left">
                  <p class="font-bold text-primary50">Nome do modelo</p>
                  <p class="text-sm text-primary50">{{ template.name }}</p>
                </div>
                <outlined-button
                  text="Editar"
                  width="110px"
                  :prepend-icon="mdiPencilOutline"
                  @click="openEditModal(idx)"
                />
                <outlined-button
                  text="Excluir"
                  width="110px"
                  :prepend-icon="mdiTrashCanOutline"
                  class="!border-negative !text-negative !bg-white"
                  @click="removeTemplate(idx)"
                />
              </div>
              <outlined-button
                text="Novo modelo"
                width="150px"
                :prepend-icon="mdiPlus"
                @click="openNewModal"
              />
              <p v-if="errors.templates" class="text-red-600 text-xs mt-1">
                {{ errors.templates }}
              </p>
            </div>
          </div>
        </div>
      </v-expand-transition>

      <!--
        F2 (§8.4) — seções NOVAS da política, abaixo de todas as existentes. As de
        "Solicitação automática" (F3) e "Pós-plantão" (F4) não entram nesta fase.
      -->
      <div class="mt-6" data-testid="policy-section-activity-records">
        <CollapsibleSection
          title="Registro de Atividade · Jornada"
          test-id="policy-activity-records"
        >
          <template #title-prefix>
            <span
              v-if="!canEditRegistration"
              class="mr-2 rounded-full border border-negative/40 bg-negative/10 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-negative"
              data-testid="policy-section-activity-records-lock"
            >
              🔒 leitura
            </span>
          </template>

          <div class="flex flex-col gap-4 text-left">
            <div>
              <DsSwitch
                :model-value="activityRecordsEnabled"
                :disabled="!canEditRegistration"
                data-testid="policy-activity-records-toggle"
                label="Aceitar Registro de Atividade nesta escala"
                @update:model-value="activityRecordsEnabled = $event"
              />
              <p class="text-xs text-neutral50 m-0 mt-1">
                Desligado, o hub “Enviar registro” da vaga só oferece Registro de Plantão e nenhuma
                solicitação de atividade é criada nesta escala.
              </p>
            </div>

            <div>
              <p class="text-sm font-bold text-neutral50 m-0">Tipos aceitos nesta escala</p>
              <p class="text-xs text-neutral50 m-0" data-testid="policy-activity-records-summary">
                {{ acceptedActivityTypesSummary }}
              </p>

              <div class="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                <v-checkbox
                  v-for="type in SELECTABLE_ACTIVITY_TYPES"
                  :key="type"
                  :model-value="acceptedActivityTypes.includes(type)"
                  :data-testid="`policy-activity-type-${type}`"
                  :label="ACTIVITY_TYPE_LABELS[type]"
                  :disabled="!canEditRegistration"
                  density="compact"
                  color="primary"
                  hide-details="auto"
                  @update:model-value="toggleActivityType(type, $event)"
                />
              </div>

              <p
                v-if="isActivityTypesMissing"
                class="text-red-600 text-xs mt-1 m-0"
                data-testid="policy-activity-types-error"
              >
                Selecione ao menos um tipo
              </p>
              <p class="text-xs text-neutral50 m-0 mt-1">
                Desmarcar um tipo não invalida registros já enviados com ele. “Ajuste
                administrativo” não é configurável por escala: só a auditoria o oferece, nunca o hub
                do escalista.
              </p>
            </div>
          </div>
        </CollapsibleSection>
      </div>

      <!--
        F3 (M4) — a cobrança automática vem logo depois do toggle que a habilita:
        sem "Aceitar Registro de Atividade" ligado, nenhuma solicitação é criada.
      -->
      <div class="mt-4" data-testid="policy-section-activity-request">
        <ActivityRequestSection
          :monitoring-type="monitoringType"
          :request-after-checkout-hours-app="requestAfterCheckoutHoursApp"
          :request-after-days-sheet="requestAfterDaysSheet"
          :request-after-hours-external="requestAfterHoursExternal"
          :requires-app-check="requiredCheckOurTeam"
          :activity-records-enabled="activityRecordsEnabled"
          :readonly="!canEditRegistration"
          :errors="errors"
          @update:request-after-checkout-hours-app="
            setRequestDelay('requestAfterCheckoutHoursApp', $event)
          "
          @update:request-after-days-sheet="setRequestDelay('requestAfterDaysSheet', $event)"
          @update:request-after-hours-external="
            setRequestDelay('requestAfterHoursExternal', $event)
          "
          :send-window-hours-app="activityRequestSendWindowHoursApp"
          :send-window-days-sheet="activityRequestSendWindowDaysSheet"
          :sheet-recurrences="sheetRecurrences"
          @update:send-window-hours-app="
            setRequestDelay('activityRequestSendWindowHoursApp', $event)
          "
          @update:send-window-days-sheet="
            setRequestDelay('activityRequestSendWindowDaysSheet', $event)
          "
        />
      </div>

      <div class="mt-4" data-testid="policy-section-source-precedence">
        <CollapsibleSection
          title="Precedência das fontes (por perna)"
          test-id="policy-source-precedence"
        >
          <template #title-prefix>
            <span
              v-if="!canEditRegistration"
              class="mr-2 rounded-full border border-negative/40 bg-negative/10 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-negative"
              data-testid="policy-section-source-precedence-lock"
            >
              🔒 leitura
            </span>
          </template>

          <SourcePrecedenceList v-model="sourcePrecedence" :disabled="!canEditRegistration" />
        </CollapsibleSection>
      </div>

      <!--
        Frente B (spec antecipe-registration §6, Q15/Q26) — fontes aceitas por modalidade de
        pagamento prioritário. Mesma trava da Q31. A seção só existe quando o backend PROVA
        que conhece o campo (`hasPaymentSourceAcceptance`), pelo mesmo motivo do gate do
        pós-plantão: um backend anterior descartaria o valor em silêncio.
      -->
      <div
        v-if="hasPaymentSourceAcceptance"
        class="mt-4"
        data-testid="policy-section-payment-source-acceptance"
      >
        <CollapsibleSection
          title="Fontes aceitas para Antecipe e À vista"
          test-id="policy-payment-source-acceptance"
        >
          <template #title-prefix>
            <span
              v-if="!canEditRegistration"
              class="mr-2 rounded-full border border-negative/40 bg-negative/10 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-negative"
              data-testid="policy-section-payment-source-acceptance-lock"
            >
              🔒 leitura
            </span>
          </template>

          <PaymentSourceAcceptanceMatrix
            v-model="paymentSourceAcceptance"
            :disabled="!canEditRegistration"
          />
        </CollapsibleSection>
      </div>

      <!--
        F4 (M4, seção "Pós-plantão" / spec §7.2b) — o gate do pós-plantão. A
        seção só existe quando o backend PROVA que conhece o campo (ver
        `hasPostStationGate`): oferecer a chave a um backend anterior à F4 seria
        prometer uma configuração que ninguém grava.
      -->
      <div v-if="hasPostStationGate" class="mt-4" data-testid="policy-section-post-station-gate">
        <CollapsibleSection title="Pós-plantão" test-id="policy-post-station-gate">
          <template #title-prefix>
            <span
              v-if="!canEditRegistration"
              class="mr-2 rounded-full border border-negative/40 bg-negative/10 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-negative"
              data-testid="policy-section-post-station-gate-lock"
            >
              🔒 leitura
            </span>
          </template>

          <div class="flex flex-col gap-3 text-left">
            <div>
              <DsSwitch
                :model-value="postStationBlockOnRegistration"
                :disabled="!canEditRegistration"
                data-testid="policy-post-station-gate-toggle"
                :label="POST_STATION_GATE_LABEL"
                @update:model-value="postStationBlockOnRegistration = $event"
              />
              <p class="text-xs text-neutral50 m-0 mt-1">
                Desligado, o estado do Registro de Plantão aparece apenas como informação na tela de
                pós-plantão. Ligado, o pós-plantão não pode ser aprovado enquanto houver plantão
                fora do estado “Registro OK”.
              </p>
            </div>

            <!--
              Previsão de impacto (M5, balão ⑥ / spec §7.2b): o número vive no
              ponto de DECISÃO, logo abaixo da chave que ele qualifica. O painel
              de pós-plantão mostra o mesmo número, mas lá ele é consequência. O
              componente some sozinho sem escala, sem período ou com backend
              anterior à rota — e o texto é neutro quanto ao estado atual do
              toggle, porque a tela não sabe, em todo recorte, se ele já está
              ligado.
            -->
            <RegistrationBlockImpactNotice
              :schedule-id="props.scheduleId ?? null"
              :from="blockImpactRange.from"
              :to="blockImpactRange.to"
              test-id="policy-block-impact"
            />

            <table
              class="w-full text-left text-xs text-neutral50"
              data-testid="policy-post-station-gate-states"
            >
              <thead>
                <tr class="border-b">
                  <th class="py-1 pr-3 font-bold">Estado</th>
                  <th class="py-1 pr-3 font-bold">Significado</th>
                  <th class="py-1 font-bold">Com o bloqueio ligado</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="state in POST_STATION_GATE_STATES" :key="state.label" class="border-b">
                  <td class="py-1 pr-3 font-bold whitespace-nowrap">{{ state.label }}</td>
                  <td class="py-1 pr-3">{{ state.meaning }}</td>
                  <td class="py-1 whitespace-nowrap">{{ state.effect }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CollapsibleSection>
      </div>

      <p v-if="errors._general" class="text-red-600 text-sm mt-2">{{ errors._general }}</p>
    </div>

    <!--
      Aviso ① de M4: desligar o Registro de Atividade com registros vivos não apaga nada —
      eles continuam na fila e podem ser auditados. A confirmação existe para que ninguém
      desligue achando que a fila esvazia junto.
    -->
    <div
      v-if="pendingDisableConfirmation"
      class="flex flex-wrap items-center gap-3 rounded-lg border border-alert/40 bg-alert/10 px-3 py-2.5 text-left text-sm"
      data-testid="policy-activity-records-disable-confirm"
    >
      <span>
        Existem {{ openActivityRecordsCount }} registros em aberto; eles continuam na fila e podem
        ser auditados. Confirmar?
      </span>
      <div class="ml-auto flex gap-2">
        <button
          type="button"
          class="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-bold text-neutral50"
          data-testid="policy-activity-records-disable-cancel"
          @click="pendingDisableConfirmation = false"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="rounded-md bg-primary30 px-3 py-1.5 text-xs font-bold text-white"
          data-testid="policy-activity-records-disable-accept"
          @click="confirmDisableAndSubmit()"
        >
          Confirmar
        </button>
      </div>
    </div>

    <div v-if="!createSchedule" class="flex justify-end mt-2 gap-4 border-t pt-4 shrink-0 bg-white">
      <outlined-button text="Cancelar" width="140px" @click="$emit('cancel')" />
      <filled-button
        data-testid="policy-save"
        :text="isEditing ? 'Salvar Alterações' : 'Criar Política'"
        width="200px"
        @click="submitPolicy()"
        :loading="dsAttendanceStore.isLoadingPolicies"
        :disabled="isCheckMethodMissing || isActivityTypesMissing || isCheckDisableUnconfirmed"
      />
    </div>

    <SheetTemplateModal
      :show="showSheetModal"
      :initial-data="templateToEdit"
      :monitoring-type="monitoringType"
      @close="closeModal"
      @save="handleSheetModalSave"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, toRef } from 'vue'
import DsSwitch from '@/components/designSystem/Switch/index.vue'
import { mdiCalendar, mdiTrashCanOutline, mdiPencilOutline, mdiPlus } from '@mdi/js'
import { DatePicker } from 'v-calendar'
import { useCreateScheduleStore } from '@/pinia-store/useCreateScheduleStore'
import { useDsAttendanceStore } from '@/pinia-store/useDsAttendanceStore'
import { step3Schema } from './schemas'

import EditableHeader from '@/components/designSystem/Inputs/EditableHeader.vue'
import OptionSelect from '@/components/designSystem/Select/OptionSelect.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import SheetTemplateModal from '@/components/designSystem/modals/SheetTemplateModal.vue'
import SegmentedControl from '@/components/designSystem/Inputs/SegmentedControl.vue'
import PermanenceToggleField from './PermanenceToggleField.vue'
import CollapsibleSection from '@/modules/company/Dashboard/components/CollapsibleSection.vue'
import SourcePrecedenceList from './SourcePrecedenceList.vue'
import ActivityRequestSection from './ActivityRequestSection.vue'
import ScheduleStepHeading from './ScheduleStepHeading.vue'
import { DEFAULT_SOURCE_PRECEDENCE, sanitizeSourcePrecedence } from './sourcePrecedence'
import PaymentSourceAcceptanceMatrix from './PaymentSourceAcceptanceMatrix.vue'
import {
  materializePaymentSourceAcceptance,
  modalitiesWithoutSource,
  paymentSourceAcceptanceForSave,
  type PaymentSourceAcceptanceMatrix as PaymentSourceAcceptanceMatrixValue
} from './paymentSourceAcceptance'
import type { PaymentSourceAcceptance } from '@/types/payment-eligibility.types'
import {
  AUDIT_DEADLINE_HELP,
  buildAuditDeadlinePreview,
  normalizeAuditDeadlineHours
} from './auditDeadline'
import {
  DEFAULT_CHECK_METHOD,
  formatVigencyDate,
  SCHEDULE_DEFAULT_CHECK_FLAGS,
  type CurrentCheckRequirement,
  type ScheduleCheckFlags
} from '@/utils/registrationCheck'
import { useRegistrationPolicyPermissions } from '@/modules/company/Policy/composables/useRegistrationPolicyPermissions'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'

import { dayOptions } from '@/modules/company/Dashboard/modals/utils/data'
import {
  ActivityType,
  ACTIVITY_TYPE_LABELS,
  CheckMethod,
  DSAttendanceMonitoringType,
  DSAttendanceDeadlineCalculationType,
  DSAttendanceRuleRecurrence,
  DSATTENDANCE_MONITORING_TYPE_OPTIONS,
  DSATTENDANCE_DEADLINE_CALCULATION_OPTIONS,
  DSATTENDANCE_RECURRENCE_OPTIONS,
  CHECK_METHOD_OPTIONS,
  hasRulesAndTemplates,
  NOT_OUR_TEAM_CHECK_DISABLED_REASON
} from '@/constants/ds-attendance.constants'
import type { EffectiveSourceKind } from '@/constants/ds-attendance.constants'
import { SCHEDULE_SECTION_LABELS } from '@/constants/schedule-sections.constants'
import {
  AUTO_APPROVAL_OPTIONS,
  buildAutoApprovalCriteriaText,
  DEFAULT_AUTO_APPROVAL,
  DEFAULT_MANUAL_REVIEW_SAMPLE_RATE,
  DEFAULT_SHEET_CONFERENCE_BY,
  DSAttendanceAutoApproval,
  DSAttendanceSheetConferenceBy,
  MANUAL_REVIEW_SAMPLE_RATE_HELP,
  normalizeManualReviewSampleRate,
  SHEET_CONFERENCE_BY_HELP,
  SHEET_CONFERENCE_BY_OPTIONS
} from '@/constants/ds-attendance-auto-approval.constants'

import type {
  DSAttendancePolicyData,
  DSAttendancePolicyRule,
  DSAttendanceSheetTemplate
} from '@/types/ds-attendance.types'

import { ValidationError } from 'yup'
import { parseLocalDate } from '@/utils/date'
import { useToast } from 'vue-toastification'
import { useScheduleMaxDate } from '@/hooks/useScheduleMaxDate'
import RegistrationBlockImpactNotice from '@/modules/company/PostStation/components/RegistrationBlockImpactNotice.vue'
import { REGISTRATION_GATE_SETTING_NAME } from '@/modules/company/PostStation/constants'

/**
 * Rótulo do toggle do gate. O nome do ajuste vem da MESMA constante que o painel
 * de pós-plantão cita nos seus banners ("… está ligado nesta escala"): se as duas
 * telas divergirem, o gestor não reconhece na política o ajuste que a auditoria
 * culpa pelo bloqueio.
 */
const POST_STATION_GATE_LABEL = `${REGISTRATION_GATE_SETTING_NAME} para aprovar o pós-plantão`

/** Tabela estado × efeito de M4, seção "Pós-plantão" (espelha a §7.2b do spec). */
const POST_STATION_GATE_STATES = [
  {
    label: 'Registro OK',
    meaning: 'As duas pernas têm horário efetivo de uma fonte aceita.',
    effect: 'Libera a aprovação'
  },
  {
    label: 'Parcial',
    meaning: 'Só uma perna resolvida (ex.: check-in do app, sem check-out).',
    effect: 'Bloqueia'
  },
  {
    label: 'Pendente',
    meaning: 'Existe registro aguardando auditoria ou solicitação aberta.',
    effect: 'Bloqueia'
  },
  {
    label: 'Sem registro',
    meaning: 'Nenhum registro em nenhum canal e nenhuma solicitação aberta.',
    effect: 'Bloqueia'
  }
] as const

const props = withDefaults(
  defineProps<{
    initialData?: DSAttendancePolicyData | null
    fullWidthModal?: boolean
    createSchedule?: boolean
    availableShifts?: { title: string; value: number }[]
    scheduleEndDate?: string | null
    /**
     * Cadeado da Q31. Ausente = a própria tela consulta a permissão; a aba da escala
     * passa o valor que já calculou para não repetir a consulta por modal aberto.
     */
    canEditRegistrationSection?: boolean
    /** Registros de atividade vivos na escala — só serve ao aviso ① de M4. */
    openActivityRecords?: number | null
    /**
     * Escala da política. Só serve ao aviso ⑥ de M5 (previsão de impacto do
     * gate do pós-plantão); ausente, a previsão simplesmente não é renderizada.
     */
    scheduleId?: number | null
    /**
     * M7 (§3, balões ⑤/⑥) — HERANÇA INICIAL. As flags legadas da escala
     * (`requiredCheckOurTeam` / `requiredCheckNotOurTeam`) não aparecem em tela
     * nenhuma, mas são elas que hoje decidem se o médico vê o botão de check-in.
     * Uma política NOVA nasce com esse estado, nunca desligada por omissão.
     * Ausente (passo 3 do criar escala, onde ainda não existe escala), cai no
     * default do produto — ver `SCHEDULE_DEFAULT_CHECK_FLAGS`.
     */
    inheritedCheckFlags?: ScheduleCheckFlags | null
    /**
     * M7 (§4) — o que a escala exige HOJE: a política vigente, se houver, senão a
     * flag legada. É a linha de base do aviso de desligamento; sem ela, nenhum
     * aviso aparece (a tela não inventa um estado que não conhece).
     */
    currentCheckRequirement?: CurrentCheckRequirement | null
  }>(),
  {
    // Sem o default explícito, o boolean casting do Vue transformaria a prop AUSENTE
    // em `false` e o `??` abaixo nunca cairia no fallback: quem monta o formulário
    // sem passá-la travaria as seções em leitura para todo mundo.
    canEditRegistrationSection: undefined
  }
)

/**
 * Recorte do aviso ⑥: "N plantões DESTE MÊS", como a spec §7.2b escreve. Um
 * período configurável aqui seria mais um campo para o gestor entender antes de
 * responder a pergunta que ele veio responder (ligar ou não o gate).
 */
const blockImpactRange = computed(() => {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  const iso = (date: Date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
  return {
    from: iso(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: iso(new Date(now.getFullYear(), now.getMonth() + 1, 0))
  }
})

const emit = defineEmits<{
  (e: 'save', data: DSAttendancePolicyData): void
  (e: 'cancel'): void
}>()

const store = useCreateScheduleStore()
const dsAttendanceStore = useDsAttendanceStore()
const { flags } = useFeatureFlags()
const errors = ref<Record<string, string>>({})
const toast = useToast()

const { scheduleMaxDate } = useScheduleMaxDate(toRef(props, 'scheduleEndDate'))

const policyId = ref<string | number | undefined>(undefined)
const policyName = ref('')
const startDate = ref<Date | null>(null)
const endDate = ref<Date | null>(null)
const isIndeterminate = ref(false)
const monitoringType = ref<DSAttendanceMonitoringType>(DSAttendanceMonitoringType.MINHA_ESCALA_APP)
const externalApp = ref<string | null>(null)
const requiredCheckOurTeam = ref<boolean>(false)
const requiredCheckNotOurTeam = ref<boolean>(false)
const checkMethodOurTeam = ref<CheckMethod | null>(null)
const checkMethodNotOurTeam = ref<CheckMethod | null>(null)
const autoPermanenceOurTeam = ref<boolean>(true)
const autoPermanenceNotOurTeam = ref<boolean>(true)
const checkinLateToleranceMinutes = ref<number | null>(null)
const checkoutEarlyToleranceMinutes = ref<number | null>(null)
const submissionDeadlineHours = ref<number | null>(null)
const deadlineCalculationType = ref<DSAttendanceDeadlineCalculationType | null>(null)
/** Prazo de auditoria (§7.4). `string` porque `v-model.number` limpo devolve `''`. */
const auditDeadlineHours = ref<number | string | null>(null)
const auditDeadlineCalculationType = ref<DSAttendanceDeadlineCalculationType | null>(null)
const auditDeadlinePreview = computed(() =>
  buildAuditDeadlinePreview(normalizeAuditDeadlineHours(auditDeadlineHours.value))
)

/** Seção "Auditoria" (§6.1) — nasce nos padrões do spec, não em `null`. */
const sheetConferenceBy = ref<DSAttendanceSheetConferenceBy>(DEFAULT_SHEET_CONFERENCE_BY)
const autoApproval = ref<DSAttendanceAutoApproval>(DEFAULT_AUTO_APPROVAL)
/**
 * `number | string` não é frouxidão: `v-model.number` num `v-text-field` limpo devolve string
 * vazia, e é `normalizeManualReviewSampleRate` que traduz isso para 0 na hora de montar o
 * payload — o tipo aqui só reconhece o que o campo realmente devolve.
 */
const manualReviewSampleRate = ref<number | string>(DEFAULT_MANUAL_REVIEW_SAMPLE_RATE)

/* -------------------------------------------------------------------------
 * F2 (§1.5 / §8.4) — Registro de Atividade e precedência das fontes
 * ------------------------------------------------------------------------- */

/**
 * `ADMIN_ADJUSTMENT` fica FORA da lista por escala (Q4/Q10): o ajuste administrativo é
 * ofertado só dentro da auditoria, nunca no hub do escalista. Deixá-lo configurável aqui
 * tiraria o dente da regra — bastaria marcá-lo para reabrir a edição manual por fora.
 */
const SELECTABLE_ACTIVITY_TYPES: ActivityType[] = Object.values(ActivityType).filter(
  (type) => type !== ActivityType.ADMIN_ADJUSTMENT
)

/** `acceptedActivityTypes` nulo no backend significa "todos os configuráveis". */
const DEFAULT_ACCEPTED_ACTIVITY_TYPES: ActivityType[] = [...SELECTABLE_ACTIVITY_TYPES]

/** Default OFF (§1.5): ligar o Registro de Atividade é o rollout, escala a escala. */
const activityRecordsEnabled = ref<boolean>(false)
const acceptedActivityTypes = ref<ActivityType[]>([...DEFAULT_ACCEPTED_ACTIVITY_TYPES])
const sourcePrecedence = ref<EffectiveSourceKind[]>(sanitizeSourcePrecedence(null))
/**
 * As duas listas COMO O BACKEND DEVOLVEU (`null` = "esta escala não customizou"). A tela
 * mostra o default materializado (6 tipos, ordem de fábrica), mas o save devolve o valor
 * cru enquanto o usuário não mexer: o gate de `attendance_registration_configuring` compara
 * o payload com o que está gravado, e `null` → lista default contava como alteração da
 * seção protegida — um Editor Intermediário tomava 403 salvando só a tolerância (A1/1.04).
 */
const loadedAcceptedActivityTypes = ref<ActivityType[] | null>(null)
const loadedSourcePrecedence = ref<EffectiveSourceKind[] | null>(null)

/** `null`/vazio ou só tipos desconhecidos → todos os configuráveis, na ordem do catálogo. */
const normalizeAcceptedActivityTypes = (raw?: ActivityType[] | null): ActivityType[] => {
  const accepted = raw?.filter((type) => SELECTABLE_ACTIVITY_TYPES.includes(type))
  return accepted?.length
    ? SELECTABLE_ACTIVITY_TYPES.filter((type) => accepted.includes(type))
    : [...DEFAULT_ACCEPTED_ACTIVITY_TYPES]
}

const sameList = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((item, index) => item === b[index])

/** Lista gravada que o backend aceitaria de volta: não vazia, sem repetição, só valores do catálogo. */
const isValidStoredList = <T extends string>(raw: T[] | null, catalog: readonly T[]): raw is T[] =>
  raw !== null &&
  raw.length > 0 &&
  new Set(raw).size === raw.length &&
  raw.every((item) => catalog.includes(item))

/**
 * O que vai no payload: o valor cru enquanto a tela ainda mostra o que foi carregado
 * (`null` continua `null`), senão a lista da tela. Valor cru inválido (dado legado —
 * lista vazia, repetida ou fora do catálogo) também vira a lista da tela: devolvê-lo
 * reprovaria na validação e travaria o save inteiro sem o usuário ter tocado em nada.
 */
const acceptedActivityTypesForSave = (): ActivityType[] | null => {
  const raw = loadedAcceptedActivityTypes.value
  const untouched = sameList(acceptedActivityTypes.value, normalizeAcceptedActivityTypes(raw))
  if (!untouched) return [...acceptedActivityTypes.value]
  if (raw === null) return null
  return isValidStoredList(raw, Object.values(ActivityType))
    ? raw
    : [...acceptedActivityTypes.value]
}

const sourcePrecedenceForSave = (): EffectiveSourceKind[] | null => {
  const raw = loadedSourcePrecedence.value
  const untouched = sameList(sourcePrecedence.value, sanitizeSourcePrecedence(raw))
  if (!untouched) return [...sourcePrecedence.value]
  if (raw === null) return null
  return isValidStoredList(raw, DEFAULT_SOURCE_PRECEDENCE) ? raw : [...sourcePrecedence.value]
}
/**
 * F3 (§1.5 / M4) — prazos da solicitação automática, por canal. `null` = "usar o default
 * do produto" (a tela só mostra o default no placeholder, nunca o materializa) e `0` =
 * canal desligado. Nascem em `null` para que uma política anterior à F3 não passe a
 * gravar um prazo que ninguém escolheu.
 */
const requestAfterCheckoutHoursApp = ref<number | null>(null)
const requestAfterDaysSheet = ref<number | null>(null)
const requestAfterHoursExternal = ref<number | null>(null)

/** Q18 — janela de envio (spec 2026-09-25 §2.4). `null` = padrão do produto. */
const activityRequestSendWindowHoursApp = ref<number | null>(null)
const activityRequestSendWindowDaysSheet = ref<number | null>(null)

/**
 * F4 (§7.2b / M4, seção "Pós-plantão") — com `true`, o domínio "Registro de
 * Plantão" deixa de ser informativo e passa a barrar a aprovação do pós-plantão.
 *
 * A tela NÃO escolhe o valor inicial: políticas existentes nascem com o gate
 * ligado pelo backfill da migration da F4 e o default de política nova é decisão
 * do dono do produto, materializada numa constante do backend. Aqui só se mostra
 * o que veio no GET (ver `loadPostStationGateField`).
 */
const postStationBlockOnRegistration = ref<boolean>(false)

/**
 * A política carregada trouxe o campo? `null` = nenhuma política carregada
 * ainda (política NOVA). Numa política existente, a PRESENÇA do campo na
 * resposta é a única prova de que o backend da F4 está no ar.
 */
const loadedPolicyHasGate = ref<boolean | null>(null)

/**
 * Tolerância a backend anterior à F4: sem a prova acima, a seção não aparece e o
 * campo não entra no payload. Numa política nova não há resposta que prove nada,
 * e o fallback é a mesma feature detection que governa o cadeado da Q31 — o
 * backend do pacote de registro sobe inteiro, não campo a campo.
 */
const hasPostStationGate = computed(
  () => loadedPolicyHasGate.value ?? dsAttendanceStore.isRegistrationBackendAvailable
)

/* -------------------------------------------------------------------------
 * Frente B (spec antecipe-registration §6, Q15/Q26) — fontes aceitas para Antecipe/À vista
 * ------------------------------------------------------------------------- */

const paymentSourceAcceptance = ref<PaymentSourceAcceptanceMatrixValue>(
  materializePaymentSourceAcceptance(null)
)
/** Como o backend devolveu (`null` = default). É o que volta no save enquanto ninguém mexe. */
const loadedPaymentSourceAcceptance = ref<PaymentSourceAcceptance | null>(null)
/** `null` = política nova; senão, a PRESENÇA da chave no GET prova o backend da frente B. */
const loadedPolicyHasAcceptance = ref<boolean | null>(null)

const hasPaymentSourceAcceptance = computed(
  () =>
    flags.enabledPaymentEligibility &&
    (loadedPolicyHasAcceptance.value ?? dsAttendanceStore.isRegistrationBackendAvailable)
)

const missingAcceptanceModalities = computed(() =>
  modalitiesWithoutSource(paymentSourceAcceptance.value)
)

/** Seção na tela com alguma modalidade sem fonte: trava o salvar (a API responderia 400). */
const isPaymentSourceAcceptanceIncomplete = computed(
  () => hasPaymentSourceAcceptance.value && missingAcceptanceModalities.value.length > 0
)

const REQUEST_DELAY_REFS = {
  requestAfterCheckoutHoursApp,
  requestAfterDaysSheet,
  requestAfterHoursExternal,
  activityRequestSendWindowHoursApp,
  activityRequestSendWindowDaysSheet
} as const

const setRequestDelay = (field: keyof typeof REQUEST_DELAY_REFS, value: number | null) => {
  REQUEST_DELAY_REFS[field].value = value
  delete errors.value[field]
}

/** Valor com que a política foi carregada — o aviso ① só vale para quem DESLIGA. */
const initialActivityRecordsEnabled = ref<boolean>(false)
const pendingDisableConfirmation = ref(false)

// Nome diferente do da prop de propósito: a prop é o valor que a aba já calculou e
// tem precedência; este é o fallback para quem monta o formulário sem passá-la.
const { canEditRegistrationSection: hasRegistrationPermission, canEditAuditSection } =
  useRegistrationPolicyPermissions()

const canEditRegistration = computed(
  () => props.canEditRegistrationSection ?? hasRegistrationPermission.value
)

/**
 * C3 — a seção "Auditoria" já está em PROD e NÃO é governada pela permissão da Q31:
 * ela só vira leitura depois de o backend recusar o salvamento com 403. Sem essa
 * separação, o deploy do web travaria para todo mundo uma tela entregue na 1.10.647.
 */
const canEditAudit = computed(() => canEditAuditSection.value)

const openActivityRecordsCount = computed(() => props.openActivityRecords ?? 0)

const acceptedActivityTypesSummary = computed(
  () =>
    `${acceptedActivityTypes.value.length} de ${SELECTABLE_ACTIVITY_TYPES.length} tipos aceitos · ` +
    'justificativa obrigatória para “Outros”.'
)

/** Aviso ② de M4: sem nenhum tipo, o hub não teria o que oferecer. */
const isActivityTypesMissing = computed(
  () => activityRecordsEnabled.value && acceptedActivityTypes.value.length === 0
)

const toggleActivityType = (type: ActivityType, checked: unknown) => {
  if (!canEditRegistration.value) return
  const next = new Set(acceptedActivityTypes.value)
  if (checked) next.add(type)
  else next.delete(type)
  acceptedActivityTypes.value = SELECTABLE_ACTIVITY_TYPES.filter((item) => next.has(item))
}

/**
 * Texto de ajuda da aprovação automática. As tolerâncias entram no texto porque são as desta
 * política: o critério central da §6.3 reusa exatamente esses dois números, e o gestor precisa
 * ver qual folga ele está autorizando.
 */
const autoApprovalCriteriaText = computed(() =>
  buildAutoApprovalCriteriaText({
    checkinLateToleranceMinutes: checkinLateToleranceMinutes.value,
    checkoutEarlyToleranceMinutes: checkoutEarlyToleranceMinutes.value
  })
)

const emptyRule = (): DSAttendancePolicyRule => ({
  name: '',
  shiftIds: [],
  recurrence: null,
  dayOfMonth: null,
  daysOfWeek: []
})

const rules = ref<DSAttendancePolicyRule[]>([emptyRule()])

/** Q18 — recorrências das regras, para o placeholder da janela da folha. */
const sheetRecurrences = computed(() =>
  rules.value
    .map((rule) => rule.recurrence)
    .filter((recurrence): recurrence is DSAttendanceRuleRecurrence => !!recurrence)
)
const templates = ref<DSAttendanceSheetTemplate[]>([])

const showRulesAndTemplates = computed(() => hasRulesAndTemplates(monitoringType.value))

const showSheetModal = ref(false)
const templateToEdit = ref<DSAttendanceSheetTemplate | null>(null)
const editingIndex = ref<number | null>(null)

watch(startDate, () => {
  if (errors.value.startDate) delete errors.value.startDate
})
watch(endDate, () => {
  if (errors.value.endDate) delete errors.value.endDate
})
watch(isIndeterminate, (val) => {
  if (val) {
    endDate.value = null
    if (errors.value.endDate) delete errors.value.endDate
  }
})

watch(monitoringType, (newType) => {
  delete errors.value.monitoringType
  delete errors.value.requiredCheckOurTeam
  delete errors.value.requiredCheckNotOurTeam
  delete errors.value.submissionDeadlineHours
  delete errors.value.deadlineCalculationType
  delete errors.value.rules
  delete errors.value.templates
  delete errors.value.externalApp
  delete errors.value.checkinLateToleranceMinutes
  delete errors.value.checkoutEarlyToleranceMinutes
  if (!hasRulesAndTemplates(newType)) {
    rules.value = [emptyRule()]
    templates.value = []
  }
  if (newType !== DSAttendanceMonitoringType.EXTERNAL_APP) {
    externalApp.value = null
  }
})

watch(requiredCheckOurTeam, (newOur) => {
  if (!newOur) {
    checkMethodOurTeam.value = null
    delete errors.value.checkMethodOurTeam
  }
})

/* -------------------------------------------------------------------------
 * M7 — herança inicial das flags da escala e aviso de desligamento
 * ------------------------------------------------------------------------- */

/** Estado com que os interruptores NASCERAM. `null` numa política já existente. */
const inheritanceBaseline = ref<ScheduleCheckFlags | null>(null)
const ourTeamTouched = ref(false)

/**
 * Etiqueta "herdado da configuração atual da escala" (balão ⑥): fica em cada
 * interruptor até a pessoa mexer nele. Ao mudar, some — fica claro o que foi
 * decisão de quem está criando a política.
 */
const isInheritedOurTeam = computed(() => !!inheritanceBaseline.value && !ourTeamTouched.value)
// Q3 — externos não herdam nada: o controle está sempre desligado.
const showInheritanceHint = computed(() => isInheritedOurTeam.value)

const onCheckToggled = (team: 'ourTeam') => {
  if (team === 'ourTeam') {
    ourTeamTouched.value = true
    delete errors.value.requiredCheckOurTeam
  }
}

/**
 * Aplica a herança. Só é chamada para política NOVA cujos interruptores ainda não
 * foram decididos por ninguém — ver `loadPolicyData`. O método nasce pré-selecionado
 * em Geolocalização quando o check nasce ligado (balão ⑦): não existe política "App
 * Minha Escala" com check ligado e sem método.
 */
const applyCheckInheritance = () => {
  const baseline = props.inheritedCheckFlags ?? SCHEDULE_DEFAULT_CHECK_FLAGS
  inheritanceBaseline.value = { ...baseline }
  ourTeamTouched.value = false
  requiredCheckOurTeam.value = baseline.ourTeam
  // Q3 — a herança da escala nunca liga o check de externos.
  requiredCheckNotOurTeam.value = false
  checkMethodNotOurTeam.value = null
  if (baseline.ourTeam) checkMethodOurTeam.value = checkMethodOurTeam.value ?? DEFAULT_CHECK_METHOD
}

/**
 * Fora da modalidade "App Minha Escala" não existe botão de check — logo, não há
 * exigência. Trocar a modalidade para Folha/App externo conta como DESLIGAR o check
 * (balão ⑧): o aviso amarelo e a confirmação valem também nesse caminho, porque o
 * efeito no médico é exatamente o mesmo — o botão some.
 */
const isAppModality = computed(
  () => monitoringType.value === DSAttendanceMonitoringType.MINHA_ESCALA_APP
)

const disablingOurTeam = computed(
  () =>
    props.currentCheckRequirement?.ourTeam === true &&
    !(isAppModality.value && requiredCheckOurTeam.value)
)
// Q3 — desligar o check de externos é decisão do produto, não da pessoa: não pede confirmação.
const needsCheckDisableConfirmation = computed(() => disablingOurTeam.value)

const disabledAudiencesText = computed(() => (disablingOurTeam.value ? 'o time interno' : ''))

/** Data que o aviso cita: a vigência do vínculo desta política (balão ⑪). */
const policyVigencyLabel = computed(() => formatVigencyDate(startDate.value))

const checkDisableConfirmed = ref(false)

const onDisableConfirmChange = (event: Event) => {
  checkDisableConfirmed.value = (event.target as HTMLInputElement).checked
}

/**
 * A confirmação vale para o que ESTAVA escrito no aviso. Observar o texto (e não só a
 * existência do aviso) cobre a troca de público sem passar por "nenhum desligamento":
 * confirmar o desligamento do time interno e, em seguida, religá-lo desligando os
 * externos não pode aproveitar a marcação anterior.
 */
watch(disabledAudiencesText, () => {
  checkDisableConfirmed.value = false
})

/** Balão ⑫ — o salvar só habilita com a caixa marcada. É a única trava nova. */
const isCheckDisableUnconfirmed = computed(
  () => needsCheckDisableConfirmation.value && !checkDisableConfirmed.value
)

const isCheckMethodMissing = computed(() => requiredCheckOurTeam.value && !checkMethodOurTeam.value)

const isEditing = computed(() => !!policyId.value)

const handleValidationError = (err: ValidationError) => {
  const errs = err.inner?.length ? err.inner : [err]
  errs.forEach((validationError) => {
    const key = validationError.path || '_general'
    errors.value[key] = validationError.message
  })
  toast.error(errs[0]?.message || 'Erro de validação. Verifique os campos.')
}

const buildPolicyData = (): DSAttendancePolicyData => ({
  id: policyId.value ? Number(policyId.value) : undefined,
  name: policyName.value,
  monitoringType: monitoringType.value,
  externalApp:
    monitoringType.value === DSAttendanceMonitoringType.EXTERNAL_APP ? externalApp.value : null,
  requiredCheckOurTeam: requiredCheckOurTeam.value,
  // Q3 — sempre desligado; o backend também força (PR2).
  requiredCheckNotOurTeam: false,
  checkMethodOurTeam: requiredCheckOurTeam.value ? checkMethodOurTeam.value : null,
  checkMethodNotOurTeam: null,
  autoPermanenceOurTeam: autoPermanenceOurTeam.value,
  autoPermanenceNotOurTeam: autoPermanenceNotOurTeam.value,
  checkinLateToleranceMinutes: checkinLateToleranceMinutes.value,
  checkoutEarlyToleranceMinutes: checkoutEarlyToleranceMinutes.value,
  submissionDeadlineHours: submissionDeadlineHours.value,
  deadlineCalculationType: deadlineCalculationType.value,
  // Sem horas, o cálculo vai `null` junto: é o que o PATCH do BE faz com `null` (§6.1).
  auditDeadlineHours: normalizeAuditDeadlineHours(auditDeadlineHours.value),
  auditDeadlineCalculationType:
    normalizeAuditDeadlineHours(auditDeadlineHours.value) === null
      ? null
      : auditDeadlineCalculationType.value,
  // Seção "Auditoria" (§6.1) — sempre enviada, mesmo nos padrões: é o que permite desligar a
  // aprovação automática de uma política que já a tinha ligada.
  sheetConferenceBy: sheetConferenceBy.value,
  autoApproval: autoApproval.value,
  // Campo limpo (`v-model.number` devolve string vazia) vale 0: "não sortear nada".
  // Desligada, a taxa vai 0: validar o campo escondido travaria o save sem erro visível.
  manualReviewSampleRate:
    autoApproval.value === DSAttendanceAutoApproval.FULL_MATCH
      ? normalizeManualReviewSampleRate(manualReviewSampleRate.value)
      : DEFAULT_MANUAL_REVIEW_SAMPLE_RATE,
  // F2 (§1.5) — sempre enviados, pelo mesmo motivo da seção "Auditoria": é o envio
  // explícito que permite DESLIGAR o que já tinha sido ligado. As listas voltam como
  // vieram (`null` continua `null`) enquanto o usuário não mexer nelas — ver
  // `acceptedActivityTypesForSave`.
  activityRecordsEnabled: activityRecordsEnabled.value,
  acceptedActivityTypes: acceptedActivityTypesForSave(),
  sourcePrecedence: sourcePrecedenceForSave(),
  // F3 (§1.5) — sempre enviados, e sem `?? default`: `null` é o valor que diz ao backend
  // "use o default do produto", e materializar o número aqui congelaria na política um
  // padrão que ainda pode mudar.
  requestAfterCheckoutHoursApp: requestAfterCheckoutHoursApp.value,
  requestAfterDaysSheet: requestAfterDaysSheet.value,
  requestAfterHoursExternal: requestAfterHoursExternal.value,
  activityRequestSendWindowHoursApp: activityRequestSendWindowHoursApp.value,
  activityRequestSendWindowDaysSheet: activityRequestSendWindowDaysSheet.value,
  // F4 (§7.2b) — só entra no payload quando o backend provou conhecer o campo.
  // Sem essa prova a chave é OMITIDA, e não mandada como `false`: um backend
  // anterior à F4 descartaria o `false` em silêncio (`ValidationPipe` com
  // `whitelist`) e a tela teria mentido que salvou.
  ...(hasPostStationGate.value
    ? { postStationBlockOnRegistration: postStationBlockOnRegistration.value }
    : {}),
  // Frente B — só com a seção na tela; o não tocado volta cru (`null` continua `null`).
  ...(hasPaymentSourceAcceptance.value
    ? {
        paymentSourceAcceptance: paymentSourceAcceptanceForSave(
          loadedPaymentSourceAcceptance.value,
          paymentSourceAcceptance.value
        )
      }
    : {}),
  links: [
    {
      startDate: startDate.value,
      endDate: isIndeterminate.value ? null : endDate.value
    }
  ],
  rules: rules.value.map((rule) => ({
    ...rule,
    shiftIds: rule.shiftIds ?? []
  })),
  templates: templates.value
})

/**
 * Desligar o Registro de Atividade com containers vivos não apaga nada: eles seguem na
 * fila. A confirmação (aviso ① de M4) existe para que ninguém desligue achando o
 * contrário. Só dispara para quem DESLIGA algo que estava ligado.
 */
const needsDisableConfirmation = computed(
  () =>
    !activityRecordsEnabled.value &&
    initialActivityRecordsEnabled.value &&
    openActivityRecordsCount.value > 0
)

const submitPolicy = async (options?: { skipDisableConfirmation?: boolean }) => {
  // M7, balão ⑫ — trava de segurança além do `:disabled` do botão: o passo 3 do criar
  // escala chama `submitPolicy` direto pelo "Avançar".
  if (isCheckDisableUnconfirmed.value) {
    toast.error('Confirme que o check-in deixa de ser exigido para salvar esta política.')
    return
  }

  if (isPaymentSourceAcceptanceIncomplete.value) {
    toast.error(
      'Selecione ao menos uma fonte aceita para cada modalidade de pagamento prioritário.'
    )
    return
  }

  if (needsDisableConfirmation.value && !options?.skipDisableConfirmation) {
    pendingDisableConfirmation.value = true
    return
  }

  pendingDisableConfirmation.value = false
  errors.value = {}
  const finalData = buildPolicyData()

  const dataToValidate = {
    ...finalData,
    startDate: finalData.links?.[0]?.startDate ?? null,
    endDate: finalData.links?.[0]?.endDate ?? null,
    isIndeterminate: isIndeterminate.value
  }

  try {
    await step3Schema.validate(dataToValidate, {
      abortEarly: false,
      context: { scheduleEndDate: props.scheduleEndDate ?? null }
    })
    emit('save', finalData)
  } catch (err: unknown) {
    if (err instanceof ValidationError) {
      handleValidationError(err)
    } else {
      // eslint-disable-next-line no-restricted-syntax -- falha local da validação yup, não erro de API
      toast.error('Erro ao salvar política de registro de plantão.')
      errors.value['_general'] = 'Ocorreu um erro ao salvar. Tente novamente.'
    }
  }
}

const confirmDisableAndSubmit = () => submitPolicy({ skipDisableConfirmation: true })

defineExpose({ submitPolicy, isCheckMethodMissing, isCheckDisableUnconfirmed })

const actualShifts = computed(() => {
  if (props.availableShifts && props.availableShifts.length > 0) {
    return props.availableShifts
  }
  return store.step2.shifts || []
})

const dynamicShiftOptions = computed(() => {
  return actualShifts.value.map((shift) => ({
    title: shift.title,
    value: shift.value
  }))
})

const loadLinkFields = (data: DSAttendancePolicyData) => {
  const activeLink =
    data.links?.find((link) => link.isActive) ?? data.links?.[data.links.length - 1] ?? null
  startDate.value = parseLocalDate(activeLink?.startDate)
  endDate.value = parseLocalDate(activeLink?.endDate)
  isIndeterminate.value = activeLink ? !endDate.value : false
}

const loadToleranceFields = (data: DSAttendancePolicyData) => {
  checkinLateToleranceMinutes.value = data.checkinLateToleranceMinutes ?? null
  checkoutEarlyToleranceMinutes.value = data.checkoutEarlyToleranceMinutes ?? null
  submissionDeadlineHours.value = data.submissionDeadlineHours ?? null
  deadlineCalculationType.value = data.deadlineCalculationType ?? null
  loadAuditFields(data)
}

/** Política anterior à fase 2 não tem os campos: cai nos padrões do spec, não em `null`. */
const loadAuditFields = (data: DSAttendancePolicyData) => {
  auditDeadlineHours.value = data.auditDeadlineHours ?? null
  auditDeadlineCalculationType.value = data.auditDeadlineCalculationType ?? null
  sheetConferenceBy.value = data.sheetConferenceBy ?? DEFAULT_SHEET_CONFERENCE_BY
  autoApproval.value = data.autoApproval ?? DEFAULT_AUTO_APPROVAL
  manualReviewSampleRate.value = data.manualReviewSampleRate ?? DEFAULT_MANUAL_REVIEW_SAMPLE_RATE
  loadActivityRecordFields(data)
}

/**
 * Tolerância a backend antigo (F2 ainda não existe em PROD): campos ausentes caem no
 * default do spec — `false`, os 6 tipos configuráveis e a ordem de fábrica da Q16 —,
 * nunca em `null`, para a tela não nascer num estado que o backend rejeitaria.
 */
const loadActivityRecordFields = (data: DSAttendancePolicyData) => {
  activityRecordsEnabled.value = data.activityRecordsEnabled ?? false
  initialActivityRecordsEnabled.value = activityRecordsEnabled.value
  pendingDisableConfirmation.value = false
  loadedAcceptedActivityTypes.value = data.acceptedActivityTypes ?? null
  loadedSourcePrecedence.value = data.sourcePrecedence ?? null
  acceptedActivityTypes.value = normalizeAcceptedActivityTypes(loadedAcceptedActivityTypes.value)
  sourcePrecedence.value = sanitizeSourcePrecedence(loadedSourcePrecedence.value)
  // `?? null` e nunca `|| null`: `0` (canal desligado) precisa sobreviver ao round-trip.
  requestAfterCheckoutHoursApp.value = data.requestAfterCheckoutHoursApp ?? null
  requestAfterDaysSheet.value = data.requestAfterDaysSheet ?? null
  requestAfterHoursExternal.value = data.requestAfterHoursExternal ?? null
  activityRequestSendWindowHoursApp.value = data.activityRequestSendWindowHoursApp ?? null
  activityRequestSendWindowDaysSheet.value = data.activityRequestSendWindowDaysSheet ?? null
  loadPostStationGateField(data)
  loadPaymentSourceAcceptanceField(data)
}

const loadPaymentSourceAcceptanceField = (data: DSAttendancePolicyData) => {
  const value = data.paymentSourceAcceptance
  loadedPolicyHasAcceptance.value = value !== undefined
  loadedPaymentSourceAcceptance.value = value ?? null
  paymentSourceAcceptance.value = materializePaymentSourceAcceptance(value)
}

/**
 * Sem `??` de conveniência: o campo ausente NÃO vira `false` silencioso, ele
 * apaga a seção. Fingir "desligado" para uma política que o backend nunca
 * configurou faria a tela afirmar um estado que ela não tem como saber — e o
 * backfill da F4 liga o gate justamente nas políticas existentes.
 */
const loadPostStationGateField = (data: DSAttendancePolicyData) => {
  const value = data.postStationBlockOnRegistration
  loadedPolicyHasGate.value = value !== undefined && value !== null
  postStationBlockOnRegistration.value = value === true
}

const loadMonitoringFields = (data: DSAttendancePolicyData) => {
  monitoringType.value = data.monitoringType || DSAttendanceMonitoringType.MINHA_ESCALA_APP
  externalApp.value = data.externalApp ?? null
  requiredCheckOurTeam.value = data.requiredCheckOurTeam ?? false
  // Q3 — linha antiga `true` no banco não religa o controle na tela.
  requiredCheckNotOurTeam.value = false
  checkMethodOurTeam.value = data.checkMethodOurTeam ?? null
  checkMethodNotOurTeam.value = null
  autoPermanenceOurTeam.value = data.autoPermanenceOurTeam ?? true
  autoPermanenceNotOurTeam.value = data.autoPermanenceNotOurTeam ?? true
  loadToleranceFields(data)
}

const loadRules = (data: DSAttendancePolicyData) => {
  if (data.rules?.length) {
    rules.value = data.rules.map((rule) => ({
      ...rule,
      name: rule.name ?? '',
      shiftIds: Array.isArray(rule.shiftIds) ? rule.shiftIds : [],
      daysOfWeek: Array.isArray(rule.daysOfWeek) ? rule.daysOfWeek : []
    }))
  } else {
    rules.value = [emptyRule()]
  }
}

/**
 * Política NOVA cujos interruptores ninguém decidiu ainda. `null`/`undefined` nas duas
 * flags é o que a store do criar escala e o "criar política" da aba entregam; `false`
 * explícito (usuário que já passou pelo passo 3, ou política importada) NÃO é herança.
 */
const shouldInheritScheduleCheck = (data?: DSAttendancePolicyData | null): boolean => {
  if (!data) return true
  if (data.id) return false
  return data.requiredCheckOurTeam == null && data.requiredCheckNotOurTeam == null
}

const loadPolicyData = (data?: DSAttendancePolicyData | null) => {
  const inherit = shouldInheritScheduleCheck(data)

  if (!data) {
    if (inherit) applyCheckInheritance()
    return
  }

  policyId.value = data.id ?? undefined
  policyName.value = data.name || ''
  loadLinkFields(data)
  loadMonitoringFields(data)
  loadRules(data)
  templates.value = data.templates ? JSON.parse(JSON.stringify(data.templates)) : []

  if (inherit) applyCheckInheritance()
  else inheritanceBaseline.value = null
}

watch(
  [() => props.initialData, () => props.availableShifts],
  ([newData]) => {
    loadPolicyData(newData as typeof props.initialData)
  },
  { immediate: true }
)

watch(
  () => rules.value.map((r) => r.recurrence),
  (newRecurrences) => {
    newRecurrences.forEach((recurrence, index) => {
      if (recurrence !== DSAttendanceRuleRecurrence.MONTHLY) {
        rules.value[index].dayOfMonth = null
      }
    })
  }
)

const handleHeaderSave = (val: string) => (policyName.value = val)

const addRule = () => rules.value.push(emptyRule())
const removeRule = (i: number) => rules.value.splice(i, 1)
const removeTemplate = (i: number) => templates.value.splice(i, 1)

const openNewModal = () => {
  editingIndex.value = null
  templateToEdit.value = null
  showSheetModal.value = true
}

const openEditModal = (i: number) => {
  editingIndex.value = i
  templateToEdit.value = { ...templates.value[i] }
  showSheetModal.value = true
}

const closeModal = () => {
  showSheetModal.value = false
  delete errors.value.templates
}

const handleSheetModalSave = (data: DSAttendanceSheetTemplate) => {
  if (editingIndex.value !== null) templates.value[editingIndex.value] = data
  else templates.value.push(data)
  closeModal()
}

const policySubtitle = computed(() => 'Vigência do vínculo entre escala e a política')
</script>
